<template>
  <div class="w-full flex items-center justify-center p-4" :style="{ height: height + 'px' }">
    <div v-if="hasData" class="w-full h-full flex flex-col">
      <!-- Chart Area -->
      <div class="flex-1 relative">
        <svg
          viewBox="0 0 800 300"
          class="w-full h-full"
          preserveAspectRatio="none"
        >
          <!-- Grid Lines -->
          <line
            v-for="i in 5"
            :key="`grid-${i}`"
            :x1="0"
            :y1="(i * 60)"
            :x2="800"
            :y2="(i * 60)"
            class="stroke-secondary-200 dark:stroke-secondary-700"
            stroke-width="1"
          />

          <!-- Data Lines -->
          <g v-for="(seriesName, seriesIndex) in series" :key="seriesIndex">
            <polyline
              :points="getLinePoints(seriesIndex)"
              fill="none"
              :stroke="lineColors[seriesIndex % lineColors.length]"
              stroke-width="3"
              stroke-linejoin="round"
              stroke-linecap="round"
            />

            <!-- Data Points with hover group -->
            <g
              v-for="(point, pointIndex) in chartData"
              :key="`point-${seriesIndex}-${pointIndex}`"
              class="cursor-pointer group"
              @mouseenter="hoveredPoint = { seriesIndex, pointIndex }"
              @mouseleave="hoveredPoint = null"
            >
              <circle
                :cx="getX(pointIndex)"
                :cy="getY(point.values[seriesIndex])"
                r="4"
                :fill="lineColors[seriesIndex % lineColors.length]"
                class="transition-all group-hover:r-6"
              />

              <!-- Tooltip on hover -->
              <foreignObject
                v-if="hoveredPoint?.seriesIndex === seriesIndex && hoveredPoint?.pointIndex === pointIndex"
                :x="getX(pointIndex) - 60"
                :y="getY(point.values[seriesIndex]) - 80"
                width="120"
                height="70"
                class="pointer-events-none"
              >
                <div class="flex items-center justify-center h-full">
                  <div class="bg-secondary-800 dark:bg-secondary-700 text-white px-3 py-2 rounded-lg shadow-lg text-center whitespace-nowrap">
                    <p class="text-xs font-semibold">{{ seriesName }}</p>
                    <p class="text-sm font-bold">{{ point.values[seriesIndex] }}</p>
                    <p class="text-xs text-secondary-300">{{ point.label }}</p>
                  </div>
                </div>
              </foreignObject>
            </g>
          </g>
        </svg>
      </div>

      <!-- X-Axis Labels -->
      <div class="flex justify-between mt-2 px-4">
        <span
          v-for="(point, index) in chartData"
          :key="index"
          class="text-xs text-secondary-600 dark:text-secondary-400 text-center"
          :style="{ width: `${100 / chartData.length}%` }"
        >
          {{ point.label }}
        </span>
      </div>

      <!-- Legend -->
      <div class="flex justify-center space-x-4 mt-4">
        <div
          v-for="(seriesName, index) in series"
          :key="index"
          class="flex items-center space-x-2"
        >
          <div
            class="w-4 h-1 rounded"
            :style="{ backgroundColor: lineColors[index % lineColors.length] }"
          ></div>
          <span class="text-xs font-medium text-secondary-700 dark:text-secondary-300">{{ seriesName }}</span>
        </div>
      </div>
    </div>
    <div v-else class="flex items-center justify-center h-full text-secondary-500 dark:text-secondary-400">
      <p class="text-sm">No data available</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const hoveredPoint = ref(null);

const props = defineProps({
  data: {
    type: [Array, Object],
    default: () => ({ labels: [], datasets: [] }),
  },
  height: {
    type: Number,
    default: 300,
  },
  colors: {
    type: Array,
    default: () => null,
  },
});

// Handle both old array format and new object format
const chartData = computed(() => {
  if (Array.isArray(props.data)) {
    return props.data;
  }
  if (props.data?.labels && props.data?.datasets) {
    // Convert Chart.js-style data to our format
    return props.data.labels.map((label, index) => ({
      label,
      values: props.data.datasets.map(dataset => dataset.data[index] || 0)
    }));
  }
  return [];
});

const series = computed(() => {
  if (props.data?.datasets) {
    return props.data.datasets.map(d => d.label || d.name || '');
  }
  return ['Series 1'];
});

const hasData = computed(() => chartData.value.length > 0);

const lineColors = computed(() => {
  return props.colors || ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
});

const maxValue = computed(() => {
  const allValues = chartData.value.flatMap(point => point.values || []);
  return Math.max(...allValues, 1);
});

const getX = (index) => {
  const width = 800;
  const padding = 40;
  const step = (width - padding * 2) / (chartData.value.length - 1 || 1);
  return padding + (index * step);
};

const getY = (value) => {
  const height = 300;
  const padding = 20;
  const ratio = (height - padding * 2) / maxValue.value;
  return height - padding - (value * ratio);
};

const getLinePoints = (seriesIndex) => {
  return chartData.value
    .map((point, index) => `${getX(index)},${getY(point.values[seriesIndex] || 0)}`)
    .join(' ');
};
</script>
